import React, { useState, useEffect, useCallback, useRef } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { toast } from "react-hot-toast";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Spinner } from "../shared/Loading";

import SoftBox from "../SoftBox";
import SoftInput from "../SoftInput";
import { toErrorMessage, toDisplayText } from "../../helpers/errorText";

const STATUS = {
  IDLE: "idle",
  DIRTY: "dirty",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error",
};

const MIN_SAVING_MS = 800;

/** Total-score cells store the literal number the admin typed — no % /100 normalization. */
function parseTotalScoreInput(value) {
  if (value == null || value === "") return null;
  const stripped = String(value).replace(/[$%,\s]/g, "");
  if (stripped === "" || stripped === "-") return null;
  const parsed = parseFloat(stripped);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Show stored values in the input as plain numbers. */
function toTotalScoreDisplayAmount(storedAmount) {
  const n = Number(storedAmount);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Decimals are fine to TYPE (the full value is what gets saved — see
 * parseTotalScoreInput/parsedDraft above) but are never shown once the cell
 * isn't actively being edited: initial load and post-save both re-render
 * through here. Round unconditionally, including sub-1 values (0.5 -> 1,
 * -0.4 -> 0) — no exception for magnitudes under 1.
 */
function formatTotalScoreDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n));
}

function getBorderColor(status, isDirty) {
  if (status === STATUS.ERROR) return "#F04438";
  if (status === STATUS.SAVED) return "#12B76A";
  if (isDirty) return "#7C3AED";
  return "#E9ECEF";
}

function getTooltipTitle(status, isDirty, errorMessage) {
  if (status === STATUS.SAVING) return "Saving...";
  if (status === STATUS.SAVED) return "Saved";
  if (status === STATUS.ERROR) return errorMessage || "Save failed";
  if (isDirty) return "Save value";
  return "No changes";
}

function getButtonColors(status, isDirty) {
  if (status === STATUS.SAVED) {
    return { backgroundColor: "#ECFDF3", color: "#12B76A", hoverBg: "#D1FAE5" };
  }
  if (status === STATUS.ERROR) {
    return { backgroundColor: "#F8FAFC", color: "#F04438", hoverBg: "#F1F5F9" };
  }
  if (isDirty) {
    return { backgroundColor: "#F8FAFC", color: "#7C3AED", hoverBg: "#F1F5F9" };
  }
  return { backgroundColor: "#F8FAFC", color: "#98A2B3", hoverBg: "#F1F5F9" };
}

function getStatusIcon(status) {
  if (status === STATUS.SAVING) {
    return <Spinner size={18} color="#7C3AED" />;
  }
  if (status === STATUS.SAVED) {
    return <CheckCircleOutlineIcon sx={{ fontSize: "1.25rem" }} />;
  }
  if (status === STATUS.ERROR) {
    return <ErrorOutlineIcon sx={{ fontSize: "1.25rem" }} />;
  }
  return <SaveOutlinedIcon sx={{ fontSize: "1.25rem" }} />;
}

function TotalScoreCell({
  kpiId,
  userId,
  rowIndex,
  colIndex,
  monthKey = null,
  initialAmount = 0,
  onSave,
  onAdvanceRow,
  isSavingExternal = false,
}) {
  const displayInitial = toTotalScoreDisplayAmount(initialAmount);
  const [draft, setDraft] = useState(() => formatTotalScoreDisplay(displayInitial));
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState("");
  const savingRef = useRef(false);

  const lastSavedRef = useRef(displayInitial);

  // Props are source of truth whenever the user isn't mid-edit / mid-save.
  // Do NOT keep lastSaved when it disagrees with props — that made July values
  // stick on the current-month board after a month switch remount race.
  useEffect(() => {
    if (isSavingExternal || status === STATUS.SAVING || status === STATUS.DIRTY) {
      return;
    }
    const incoming = toTotalScoreDisplayAmount(initialAmount);
    lastSavedRef.current = incoming;
    setDraft(formatTotalScoreDisplay(incoming));
    setErrorMessage("");
    if (status === STATUS.SAVED || status === STATUS.ERROR) {
      setStatus(STATUS.IDLE);
    }
  }, [initialAmount, monthKey, kpiId, userId, isSavingExternal, status]);

  const parsedDraft = parseTotalScoreInput(draft);
  const isDirty =
    status === STATUS.DIRTY ||
    (parsedDraft != null && parsedDraft !== displayInitial);

  const isSaving = status === STATUS.SAVING || isSavingExternal;

  const runSave = useCallback(async () => {
    if (savingRef.current) return;
    if (parsedDraft == null) {
      setStatus(STATUS.ERROR);
      setErrorMessage("Enter a valid number");
      return;
    }
    if (parsedDraft === displayInitial) {
      setStatus(STATUS.IDLE);
      return;
    }

    savingRef.current = true;
    setStatus(STATUS.SAVING);
    setErrorMessage("");
    const startedAt = Date.now();
    try {
      await onSave(kpiId, userId, parsedDraft);
      const waitMs = Math.max(0, MIN_SAVING_MS - (Date.now() - startedAt));
      if (waitMs > 0) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, waitMs);
        });
      }
      setStatus(STATUS.SAVED);
      lastSavedRef.current = parsedDraft;
      setDraft(formatTotalScoreDisplay(parsedDraft));
      toast.success("Score saved");
      window.setTimeout(() => setStatus(STATUS.IDLE), 2000);
    } catch (err) {
      const message = toErrorMessage(err, "Could not save");
      setStatus(STATUS.ERROR);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      savingRef.current = false;
    }
  }, [parsedDraft, displayInitial, onSave, kpiId, userId]);

  const handleKeyDown = (e) => {
    if (e.keyCode === 9) {
      e.preventDefault();
      return;
    }
    if (e.keyCode !== 13) return;
    e.preventDefault();
    runSave().then(() => {
      onAdvanceRow?.(rowIndex, colIndex, e.target);
    });
  };

  const borderColor = getBorderColor(status, isDirty);
  const buttonColors = getButtonColors(status, isDirty);
  const tooltipTitle = toDisplayText(
    getTooltipTitle(isSaving ? STATUS.SAVING : status, isDirty, errorMessage),
    "Save value",
  );

  return (
    <SoftBox display="flex" alignItems="center" gap={0.5} width="100%" position="relative" minHeight="3.25rem">
      {isSaving && (
        <SoftBox
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            borderRadius: "4px",
            backgroundColor: "rgba(255, 255, 255, 0.92)",
            pointerEvents: "all",
          }}
        >
          <Spinner size={24} color="#7C3AED" />
          <SoftBox component="span" sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#7C3AED" }}>
            Saving...
          </SoftBox>
        </SoftBox>
      )}
      <SoftInput
        id={`cell-${rowIndex}-${colIndex}`}
        type="number"
        inputProps={{ step: "any" }}
        value={draft}
        disabled={isSaving}
        onChange={(e) => {
          setDraft(e.target.value);
          setStatus(STATUS.DIRTY);
          setErrorMessage("");
        }}
        onKeyDown={handleKeyDown}
        sx={{
          flex: 1,
          height: "3.25rem !important",
          border: `2px solid ${borderColor} !important`,
          borderRadius: "6px !important",
          boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.05) !important",
          padding: "10px 14px !important",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "& .MuiInputBase-input": {
            // Match incremental score cell typography
            fontFamily: "DM Sans",
            color: "#191D23",
            fontSize: "16px",
            lineHeight: 1.2,
            fontWeight: 500,
            fontFeatureSettings: '"tnum" 1',
            letterSpacing: "-0.005em",
            verticalAlign: "unset",
          },
          "&.Mui-focused": {
            borderColor: (theme) =>
              `${status === STATUS.ERROR ? "#F04438" : theme.palette.primary.main} !important`,
            boxShadow: (theme) =>
              `0rem 0rem 0rem 0.125rem ${status === STATUS.ERROR ? "rgba(240, 68, 56, 0.25)" : theme.palette.primary.main} !important`,
          },
        }}
      />
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            type="button"
            size="small"
            disabled={!isDirty && !isSaving}
            onClick={() => runSave()}
            sx={{
              width: 36,
              height: 36,
              border: "1px solid #E9ECEF",
              borderRadius: "6px",
              backgroundColor: isSaving ? "#F3E8FF" : buttonColors.backgroundColor,
              color: isSaving ? "#7C3AED" : buttonColors.color,
              "&:hover": {
                backgroundColor: buttonColors.hoverBg,
              },
              "&.Mui-disabled": {
                color: "#CBD5E1",
                backgroundColor: "#F8FAFC",
              },
            }}
            aria-label="Save KPI value"
          >
            {getStatusIcon(isSaving ? STATUS.SAVING : status)}
          </IconButton>
        </span>
      </Tooltip>
    </SoftBox>
  );
}

export default React.memo(TotalScoreCell);
